import { useEffect, useRef } from "react"
import { useConfigFlag } from "@cloudbreakus/featurebang-react"
import { MULTI_PATIENT_ENABLED } from "constants/featureFlags"
import { useProfile } from "contexts/Profile"
import { useAlertMessage } from "hooks/useAlertMessage"

const onTheCall = (callSegments) => {
  const byAgent = new Map()
  const segments = callSegments ?? []
  segments.forEach((cs) => {
    if (!cs?.agentId || !cs?.interpreterName || cs.endTime) return
    byAgent.set(cs.agentId, cs.interpreterName)
  })
  return byAgent
}

// Only an end time means somebody left. A segment going missing, or briefly losing its name,
// means this client cannot see them -- which is not the same thing, and announcing it told
// interpreters their colleague had left while they were still on the call.
const hasHungUp = (callSegments) => {
  const segments = callSegments ?? []
  const ended = new Set()
  segments.forEach((cs) => {
    if (cs?.agentId && cs.endTime) ended.add(cs.agentId)
  })
  // An interpreter who reconnected has both an ended segment and an open one. They have not
  // left, so their open segment cancels the ended one.
  segments.forEach((cs) => {
    if (cs?.agentId && !cs.endTime) ended.delete(cs.agentId)
  })
  return ended
}

/**
 * Tells an interpreter when a colleague joins or leaves the call they are on.
 *
 * Driven by the call segments, which are pushed over a subscription, so this reacts within about
 * a second rather than on the next poll. Only interpreters with a known name are announced --
 * the subscription carries no name, so a just-joined colleague is announced once the following
 * read fills it in, rather than as "somebody".
 */
export const useCallSegmentNotifications = ({ callSegments }) => {
  const { additionalUserData } = useProfile()
  const { showInfoToast } = useAlertMessage()
  const multiPatientEnabled = useConfigFlag(MULTI_PATIENT_ENABLED)

  // Who we last knew about. Starts undefined rather than empty so the interpreters already on
  // the call when this mounts are not announced as if they had just arrived.
  const knownRef = useRef(undefined)
  const announcedDepartureRef = useRef(new Set())

  useEffect(() => {
    if (!multiPatientEnabled) return

    const present = onTheCall(callSegments)
    const previous = knownRef.current

    if (previous === undefined) {
      knownRef.current = present
      return
    }

    const me = additionalUserData?.id

    present.forEach((name, agentId) => {
      if (agentId !== me && !previous.has(agentId)) {
        showInfoToast(`${name} joined the call`)
        announcedDepartureRef.current.delete(agentId)
      }
    })

    hasHungUp(callSegments).forEach((agentId) => {
      if (agentId === me) return
      if (!previous.has(agentId)) return
      if (announcedDepartureRef.current.has(agentId)) return
      announcedDepartureRef.current.add(agentId)
      showInfoToast(`${previous.get(agentId)} left the call`)
    })

    // Keep anyone we can no longer see: losing sight of a colleague must not later read as an
    // arrival when they reappear, nor lose the name needed to announce a real departure.
    const merged = new Map(previous)
    present.forEach((name, agentId) => merged.set(agentId, name))
    knownRef.current = merged
  }, [callSegments, additionalUserData, multiPatientEnabled, showInfoToast])
}
